import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";

const LOGO_ICON_URL = "https://customer-assets.emergentagent.com/job_maternal-wellness-9/artifacts/f1y8jte1_Screenshot%202026-03-09%20at%2011.47.59%E2%80%AFPM.png";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Our North Star", path: "/north-star" },
  { name: "About the Initiative", path: "/about" },
  { name: "Framework for Action", path: "/framework" },
  { name: "Our Commitment", path: "/impact" },
  { name: "Join the Initiative", path: "/join" },
];

const linkVariants = {
  initial: { opacity: 0, y: -10 },
  animate: { opacity: 1, y: 0 },
  hover: { y: -2 }
};

const mobileNavVariants = {
  closed: { opacity: 0, x: "100%" },
  open: { 
    opacity: 1, 
    x: 0,
    transition: { type: "spring", stiffness: 300, damping: 30 }
  },
  exit: { 
    opacity: 0, 
    x: "100%",
    transition: { duration: 0.3 }
  }
};

const mobileLinkVariants = {
  closed: { opacity: 0, x: 50 },
  open: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.1, duration: 0.4, ease: [0.22, 1, 0.36, 1] }
  })
};

// Sticky CTA Banner Component - Seamless with hero
function StickyBanner({ isScrolled, isDarkHero }) {
  return (
    <motion.div
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`w-full py-2.5 px-4 text-center transition-all duration-700 ${
        isScrolled 
          ? "bg-[#0A1628] border-b border-white/5" 
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 flex-wrap">
        <p className="text-white/60 text-[11px] md:text-xs font-body tracking-wide">
          <span className="hidden sm:inline">Join governments and partners </span>
          <span>accelerating reduction of low birthweight</span>
          <span className="hidden md:inline"> and improved birth outcomes for small and sick newborns</span>
        </p>
        <Link 
          to="/join"
          className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 border border-white/10 text-white text-[10px] font-medium px-4 py-1.5 rounded-full transition-all duration-500 hover:scale-[1.02]"
        >
          Partner With Us <ArrowRight size={11} />
        </Link>
      </div>
    </motion.div>
  );
}

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const location = useLocation();
  const { scrollY } = useScroll();
  
  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious();
    if (latest > previous && latest > 150) {
      setHidden(true);
    } else {
      setHidden(false);
    }
    setIsScrolled(latest > 50);
  });

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const isDarkHero = location.pathname === "/" || 
                     location.pathname === "/about" || 
                     location.pathname === "/impact";

  return (
    <>
      {/* Sticky Banner */}
      <motion.div
        initial={{ y: -100 }}
        animate={{ y: hidden ? -100 : 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 left-0 right-0 z-50"
      >
        <StickyBanner isScrolled={isScrolled} isDarkHero={isDarkHero} />
        
        {/* Main Navbar */}
        <nav
          data-testid="navbar"
          className={`transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isScrolled
              ? "bg-white/98 backdrop-blur-2xl shadow-[0_1px_40px_rgba(0,0,0,0.06)] border-b border-slate-100/50"
              : "bg-transparent"
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo + Wordmark */}
              <Link to="/" className="relative group flex items-center gap-3" data-testid="nav-logo">
                <motion.div 
                  className="flex items-center gap-3"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                >
                  <img 
                    src={LOGO_ICON_URL} 
                    alt="Healthy Birth Initiative" 
                    className="h-10 w-10 object-cover rounded-full"
                  />
                  <span className={`hidden sm:block font-heading text-sm font-semibold tracking-tight transition-colors duration-300 ${
                    isScrolled ? "text-navy" : isDarkHero ? "text-white" : "text-navy"
                  }`}>
                    Healthy Birth Initiative
                  </span>
                </motion.div>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center gap-0.5">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.path}
                    initial="initial"
                    animate="animate"
                    whileHover="hover"
                    variants={linkVariants}
                    transition={{ delay: i * 0.05, duration: 0.3 }}
                  >
                    <Link
                      to={link.path}
                      data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                      className={`relative px-3 py-2 text-[12px] font-medium tracking-wide transition-all duration-300 rounded-full group ${
                        location.pathname === link.path
                          ? isScrolled 
                            ? "text-navy" 
                            : isDarkHero 
                              ? "text-white" 
                              : "text-navy"
                          : isScrolled
                            ? "text-slate-500 hover:text-navy"
                            : isDarkHero
                              ? "text-white/70 hover:text-white"
                              : "text-slate-500 hover:text-navy"
                      }`}
                    >
                      <span className="relative z-10">{link.name}</span>
                      {location.pathname === link.path && (
                        <motion.div
                          layoutId="activeNav"
                          className={`absolute inset-0 rounded-full ${
                            isScrolled ? "bg-navy/10" : isDarkHero ? "bg-white/10" : "bg-navy/10"
                          }`}
                          transition={{ type: "spring", stiffness: 400, damping: 30 }}
                        />
                      )}
                      <motion.span 
                        className={`absolute bottom-0.5 left-3 right-3 h-[2px] ${
                          isScrolled ? "bg-[#E8A33D]" : isDarkHero ? "bg-[#E8A33D]" : "bg-[#E8A33D]"
                        } origin-left`}
                        initial={{ scaleX: 0 }}
                        whileHover={{ scaleX: 1 }}
                        transition={{ duration: 0.3 }}
                      />
                    </Link>
                  </motion.div>
                ))}
              </div>

              {/* Mobile Menu Button */}
              <motion.button
                data-testid="mobile-menu-toggle"
                onClick={() => setIsOpen(!isOpen)}
                className={`lg:hidden relative w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${
                  isScrolled 
                    ? "bg-navy/5 text-navy hover:bg-navy/10" 
                    : isDarkHero 
                      ? "bg-white/10 text-white hover:bg-white/20" 
                      : "bg-navy/5 text-navy hover:bg-navy/10"
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <AnimatePresence mode="wait">
                  {isOpen ? (
                    <motion.div
                      key="close"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <X size={20} />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="menu"
                      initial={{ rotate: 90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: -90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Menu size={20} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </div>
          </div>
        </nav>
      </motion.div>

      {/* Mobile Menu - Full Screen Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={mobileNavVariants}
            initial="closed"
            animate="open"
            exit="exit"
            className="fixed inset-0 z-40 lg:hidden"
            data-testid="mobile-menu"
          >
            <motion.div 
              className="absolute inset-0 bg-navy"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            
            <div className="relative z-10 flex flex-col justify-center items-center h-full px-6">
              <motion.div 
                className="absolute top-6 left-6 flex items-center gap-3"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <img 
                  src={LOGO_ICON_URL} 
                  alt="HBI" 
                  className="h-10 w-10 object-cover rounded-full"
                />
                <span className="text-white font-heading text-sm font-semibold">The Healthy Birth Initiative</span>
              </motion.div>
              
              <nav className="flex flex-col items-center gap-1">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.path}
                    custom={i}
                    variants={mobileLinkVariants}
                    initial="closed"
                    animate="open"
                  >
                    <Link
                      to={link.path}
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                      className={`block text-center text-xl md:text-2xl font-heading font-medium py-2 transition-all duration-300 ${
                        location.pathname === link.path
                          ? "text-[#E8A33D]"
                          : "text-white/70 hover:text-white"
                      }`}
                    >
                      <motion.span
                        whileHover={{ x: 10 }}
                        transition={{ type: "spring", stiffness: 300 }}
                        className="inline-block"
                      >
                        {link.name}
                      </motion.span>
                    </Link>
                  </motion.div>
                ))}
              </nav>
              
              <motion.div 
                className="absolute bottom-12 left-1/2 -translate-x-1/2 text-white/20 font-body text-xs tracking-[0.3em]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                EVERY BIRTH SHOULD BE A HEALTHY BIRTH
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
